import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import AuthImage from "@/components/AuthImage";
import generateDamagePdf, { SOURCE_LABEL, SEVERITY_LABEL, STATUS_LABEL, DISPOSITION_LABEL } from "@/lib/damagePdf";
import { ArrowLeft, FileDown, CheckCircle2, AlertTriangle, Package } from "lucide-react";

const STATUS_OPTIONS = ["reported", "under_review", "action_required", "approved_for_disposal", "return_to_supplier", "markdown_approved", "transferred", "resolved", "closed"];
const DISPOSITION_OPTIONS = ["", "return_to_supplier", "dispose", "discount", "keep_display", "repair", "parts", "transfer_damage_area", "claim_carrier", "claim_supplier", "other"];

export default function DamageDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const canManage = hasAccess(user?.role, ["admin", "gm", "manager", "assistant_manager", "inventory_manager"]);
  const canSeeCosts = hasAccess(user?.role, ["admin", "gm"]);

  const load = useCallback(() => {
    api.get(`/damages/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;
  const justCreated = loc.state?.justCreated;

  const patch = async (fields) => {
    try {
      const { data } = await api.patch(`/damages/${id}`, fields);
      setD(data); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const download = async () => {
    setDownloading(true);
    try {
      await generateDamagePdf(d, { includeCosts: canSeeCosts });
      api.post(`/damages/${id}/downloaded`).catch(() => {});
      toast.success("PDF downloaded");
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto pb-10" data-testid="damage-detail-page">
      <button onClick={() => nav("/damages")} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back to Damage Log</button>

      {justCreated && (
        <div className="hos-card p-5 border border-emerald-400/50 bg-emerald-500/10 text-emerald-300 flex items-center gap-3">
          <CheckCircle2 size={20}/>
          <div>
            <div className="font-heading text-lg">Damage recorded</div>
            <div className="text-sm">{d.reference_number} · {d.quantity} unit{d.quantity !== 1 ? "s" : ""} · {SOURCE_LABEL[d.source_type]}</div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Damage Reference</div>
          <h1 className="font-heading text-3xl">{d.reference_number}</h1>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">{d.product_name || "—"} · {SOURCE_LABEL[d.source_type]}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] px-2 py-1 rounded-full border border-[color:var(--border)]">{SEVERITY_LABEL[d.severity]}</span>
          <span className="text-[10px] px-2 py-1 rounded-full border border-[color:var(--border)]">{STATUS_LABEL[d.status]}</span>
          <button className="hos-btn-primary flex items-center gap-2 text-sm" onClick={download} disabled={downloading} data-testid="damage-pdf-btn">
            <FileDown size={14}/> {downloading ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>

      <div className="hos-card p-6 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <Info label="Store" value={d.store_name || "—"}/>
        <Info label="Reported By" value={d.reported_by_name}/>
        <Info label="Date" value={new Date(d.created_at).toLocaleString()}/>
        <Info label="Product" value={d.product_name || "—"}/>
        <Info label="SKU" value={d.sku || "—"}/>
        <Info label="Barcode" value={d.barcode || "—"}/>
        <Info label="Fandom" value={d.fandom_name || "—"}/>
        <Info label="Zone" value={d.zone_name || d.display_zone_name || "—"}/>
        <Info label="Quantity" value={d.quantity}/>
        <Info label="Damage Type" value={d.damage_type}/>
        <Info label="Cause" value={d.cause}/>
        <Info label="Sellability" value={d.sellability_status}/>
        {canSeeCosts && d.unit_cost != null && <Info label="Unit Cost" value={`$${Number(d.unit_cost).toFixed(2)}`}/>}
        {canSeeCosts && d.retail_value != null && <Info label="Retail Value" value={`$${Number(d.retail_value).toFixed(2)}`}/>}
        {canSeeCosts && d.estimated_damage_cost != null && <Info label="Estimated Damage Cost" value={`$${Number(d.estimated_damage_cost).toFixed(2)}`} accent/>}
        {d.moved_to_hold && <Info label="Damage Hold" value={d.damage_hold_location || "Yes"}/>}
      </div>

      {d.source_type === "receiving" && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Linked Receiving</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Info label="Receiving Ref" value={d.receiving_reference || "—"}/>
            <Info label="Supplier" value={d.supplier_name || "—"}/>
            <Info label="Pallet" value={d.receiving_pallet_number ? `Pallet ${d.receiving_pallet_number}` : "—"}/>
            <Info label="PO" value={d.purchase_order_number || "—"}/>
            <Info label="Invoice" value={d.invoice_number || "—"}/>
          </div>
          {d.inventory_receiving_id && (
            <Link to={`/receiving/${d.inventory_receiving_id}`} className="text-[color:var(--gold)] text-sm hover:underline mt-3 inline-block">View receiving record →</Link>
          )}
        </div>
      )}

      {d.description && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-2">Description</h3>
          <p className="text-sm text-[color:var(--text-secondary)] whitespace-pre-wrap">{d.description}</p>
        </div>
      )}

      {(d.photos || []).length > 0 && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Damage Photos ({d.photos.length})</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {d.photos.map((p, i) => (
              <AuthImage key={i} photo={p} className="aspect-square w-full object-cover rounded"/>
            ))}
          </div>
        </div>
      )}

      {canManage && (
        <div className="hos-card p-6 space-y-3">
          <h3 className="font-heading text-xl">Manager / GM Review</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-caps">Status</label>
              <select className="hos-input mt-1" value={d.status} onChange={(e) => patch({ status: e.target.value })} data-testid="status-select">
                {STATUS_OPTIONS.map((k) => <option key={k} value={k}>{STATUS_LABEL[k]}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps">Disposition</label>
              <select className="hos-input mt-1" value={d.disposition || ""} onChange={(e) => patch({ disposition: e.target.value })} data-testid="disposition-select">
                {DISPOSITION_OPTIONS.map((k) => <option key={k} value={k}>{DISPOSITION_LABEL[k] || "—"}</option>)}
              </select>
            </div>
          </div>
          {(d.source_type === "receiving") && (
            <div className="border-t border-[color:var(--border)] pt-3">
              <div className="label-caps mb-2">Supplier / Carrier Claim</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!d.claim_required} onChange={(e) => patch({ claim_required: e.target.checked })}/>
                  Claim Required
                </label>
                <select className="hos-input" value={d.claim_against || ""} onChange={(e) => patch({ claim_against: e.target.value })}>
                  <option value="">Claim against…</option>
                  <option value="supplier">Supplier</option>
                  <option value="carrier">Carrier</option>
                  <option value="other">Other</option>
                </select>
                <select className="hos-input" value={d.claim_status || ""} onChange={(e) => patch({ claim_status: e.target.value })}>
                  <option value="">Claim status…</option>
                  <option value="not_started">Not Started</option>
                  <option value="submitted">Submitted</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="credited">Credited</option>
                </select>
                <input className="hos-input md:col-span-3" placeholder="Claim reference #" value={d.claim_reference || ""}
                       onChange={(e) => patch({ claim_reference: e.target.value })}/>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ label, value, accent }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className={`font-semibold ${accent ? "text-[color:var(--gold)]" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
